import React, { useEffect, useRef, useState, useMemo } from 'react';
import * as echarts from 'echarts';
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin } from 'lucide-react';
import chinaGeoJSON from '@/assets/map-data/100000_full.json';
import shanghaiGeoJSON from '@/assets/map-data/310000_full.json';
import normalCarIcon from '@/assets/normal_car.png';
import warningCarIcon from '@/assets/waring_car.png';
import errorCarIcon from '@/assets/error_car.png';
import { searchToolApi } from '@/services/api/searchTool';
import { useQuery } from '@tanstack/react-query';
import type { SearchToolResponse } from '@/services/api/searchTool'

// 计算日期差值的辅助函数
const getDateDifference = (auditDate: string): number => {
  const today = new Date();
  const audit = new Date(auditDate);
  const diffTime = Math.abs(today.getTime() - audit.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// 根据日期差值获取对应的车辆图标
const getCarIcon = (auditDate: string): string => {
  const diffDays = getDateDifference(auditDate);
  if (diffDays <= 5) {
    return normalCarIcon;
  } else if (diffDays <= 10) {
    return warningCarIcon;
  } else {
    return errorCarIcon;
  }
};

// 计算两点之间的角度
const calculateAngle = (x1: number, y1: number, x2: number, y2: number): number => {
  const deltaX = x2 - x1;
  const deltaY = y2 - y1;
  // 计算弧度并转换为角度
  const angleInRadians = Math.atan2(deltaY, deltaX);
  const angleInDegrees = (angleInRadians * 180) / Math.PI;
  return angleInDegrees;
};

// 定义状态配置的类型
interface TransportStatusConfig {
  CYCLE_DAYS: number;
  STATUS: {
    NORMAL: {
      days: number;
      color: string;
      status: string;
      icon: string;
    };
    SLOW: {
      days: number;
      color: string;
      status: string;
      icon: string;
    };
    DELAYED: {
      days?: number;
      color: string;
      status: string;
      icon: string;
    };
  };
}

// 运输状态配置
const TRANSPORT_STATUS_CONFIG: TransportStatusConfig = {
  CYCLE_DAYS: 10, // 预期运输周期10天
  STATUS: {
    NORMAL: {
      days: 5,
      color: '#22c55e',
      status: '正常',
      icon: normalCarIcon
    },
    SLOW: {
      days: 10,
      color: '#f59e0b',
      status: '较慢',
      icon: warningCarIcon
    },
    DELAYED: {
      color: '#ef4444',
      status: '延迟',
      icon: errorCarIcon
    }
  }
};

// 计算运输进度的辅助函数
const calculateTransportProgress = (auditDate: string): number => {
  const today = new Date();
  const audit = new Date(auditDate);
  const diffTime = Math.abs(today.getTime() - audit.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  // 使用配置的运输周期，最大进度为95%
  const progress = Math.min((diffDays / TRANSPORT_STATUS_CONFIG.CYCLE_DAYS) * 100, 95);
  // 确保进度至少为5%
  return Math.max(progress, 5) / 100;
};

// 获取运输状态的辅助函数
const getTransportStatus = (auditDate: string): { color: string; status: string; icon: string } => {
  const diffDays = getDateDifference(auditDate);
  const { NORMAL, SLOW, DELAYED } = TRANSPORT_STATUS_CONFIG.STATUS;
  
  if (diffDays <= NORMAL.days) {
    return NORMAL;  // 0-5天 正常
  } else if (diffDays <= SLOW.days) {
    return SLOW;    // 6-10天 较慢
  } else {
    return DELAYED; // >10天 延迟
  }
};

// 添加图例组件
const Legend = () => {
  return (
    <div className="absolute top-4 right-4 bg-white rounded-lg shadow-sm p-4 z-50">
      <h3 className="text-sm font-medium mb-2">运输状态说明</h3>
      <div className="space-y-2">
        {Object.entries(TRANSPORT_STATUS_CONFIG.STATUS).map(([key, value]) => (
          <div key={key} className="flex items-center gap-2">
            <img src={value.icon} className="w-4 h-4" alt={value.status} />
            <span className="text-xs" style={{ color: value.color }}>{value.status}</span>
            {key === 'NORMAL' && <span className="text-xs text-gray-500">（≤{value.days}天）</span>}
            {key === 'SLOW' && <span className="text-xs text-gray-500">（{TRANSPORT_STATUS_CONFIG.STATUS.NORMAL.days}-{value.days}天）</span>}
            {key === 'DELAYED' && <span className="text-xs text-gray-500">（＞{TRANSPORT_STATUS_CONFIG.STATUS.SLOW.days}天）</span>}
          </div>
        ))}
      </div>
      <div className="mt-3 pt-2 border-t">
        <div className="flex items-center gap-2">
          <div className="w-4 h-0.5 bg-blue-500"></div>
          <span className="text-xs text-gray-500">实线 - 在途</span>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <div className="w-4 h-0.5 border-t-2 border-dashed border-blue-500"></div>
          <span className="text-xs text-gray-500">虚线 - 待发货</span>
        </div>
      </div>
    </div>
  );
};

// 接口数据类型定义
interface ApiData {
  id: string;
  outWarehouse: string;    // 调出仓库
  inWarehouse: string;     // 调入仓库
  status: string;          // 状态
  createTime: string;      // 创建时间
  quantity?: number;       // 数量
  待转出数量?: number;     // 待转出数量
  待转入数量?: number;     // 待转入数量
}

interface LogisticsMapProps {
  apiData?: ApiData[]; // 使属性可选
}

// 仓库坐标配置
const warehouseLocations = {
  // 上海市内仓库
  xixiaier_haizhou: {
    id: 'xixiaier_haizhou',
    name: 'OEM西西艾尔海州仓',
    type: 'retail' as const,
    coordinates: [121.67, 30.92], // 奉贤区
    color: '#3b82f6' // 蓝色
  },
  sibei: {
    id: 'sibei',
    name: 'OEM思贝仓',
    type: 'retail' as const,
    coordinates: [121.12, 31.15], // 青浦区
    color: '#10b981' // 绿色
  },
  xuedai_product: {
    id: 'xuedai_product',
    name: 'OEM雪代成品仓',
    type: 'retail' as const,
    coordinates: [121.27, 31.38], // 嘉定区
    color: '#f59e0b' // 橙色
  },
  jinting: {
    id: 'jinting',
    name: 'OEM瑾亭航谊仓',
    type: 'retail' as const,
    coordinates: [121.57, 31.80], // 浙江湖州
    color: '#8b5cf6' // 紫色
  },
  xuedai: {
    id: 'xuedai',
    name: 'OEM雪代仓',
    type: 'retail' as const,
    coordinates: [121.07, 31.52], // 江苏省
    color: '#ec4899' // 粉色
  },
  xixiaier_huanghai: {
    id: 'xixiaier_huanghai',
    name: 'OEM西西艾尔黄海仓',
    type: 'retail' as const,
    coordinates: [121.10, 32.00], // 江苏省
    color: '#14b8a6' // 青色
  },
  // 其他仓库保持不变...
  zhengzhou: {
    id: 'zz',
    name: '郑州仓',
    type: 'factory' as const,
    coordinates: [113.665412, 34.757975] as [number, number],
  },
  guangzhou: {
    id: 'gz',
    name: '广州仓',
    type: 'factory' as const,
    coordinates: [113.280637, 23.125178] as [number, number],
  },
  yiwu: {
    id: 'yw',
    name: '义乌仓',
    type: 'factory' as const,
    coordinates: [120.071625, 29.306863] as [number, number], // 义乌市坐标
  },
  chengdu: {
    id: 'cd',
    name: '成都仓',
    type: 'factory' as const,
    coordinates: [104.065735, 30.659462] as [number, number], // 成都市坐标
  }
};

// 数据转换函数
const transformData = (rawData: any[]): ApiData[] => {
  return rawData.map(item => ({
    id: item.调拨单号,
    outWarehouse: item.调出仓库,
    inWarehouse: item.调入仓库,
    status: item.运输状态,
    createTime: item.单据审核日期,
    quantity: item.应转数量,
    待转出数量: item.待转出数量,
    待转入数量: item.待转入数量
  }));
};

// 修改路线分组的接口
interface RouteGroup {
  from: typeof warehouseLocations[keyof typeof warehouseLocations];
  to: typeof warehouseLocations[keyof typeof warehouseLocations];
  orders: ApiData[];
  color: string;
  status: string; // 添加状态字段
}

// 处理API数据，生成连线配置
const processApiData = (data: ApiData[] = [], fallbackData: any[] = []) => {
  if(data.length === 0 && fallbackData.length > 0) {
    data = transformData(fallbackData);
  }

  // 按路线和状态分组数据
  const routeGroups = new Map<string, RouteGroup>();

  data.forEach(item => {
    let fromWarehouse = null;
    let toWarehouse = null;

    // 确定调出仓库
    if (item.outWarehouse?.includes('西西艾尔海州')) {
      fromWarehouse = warehouseLocations.xixiaier_haizhou;
    } else if (item.outWarehouse?.includes('思贝')) {
      fromWarehouse = warehouseLocations.sibei;
    } else if (item.outWarehouse?.includes('雪代成品') || item.outWarehouse?.includes('207')) {
      fromWarehouse = warehouseLocations.xuedai_product;
    } else if (item.outWarehouse?.includes('瑾亭航谊')) {
      fromWarehouse = warehouseLocations.jinting;
    } else if (item.outWarehouse?.includes('雪代仓') && !item.outWarehouse?.includes('成品')) {
      fromWarehouse = warehouseLocations.xuedai;
    } else if (item.outWarehouse?.includes('西西艾尔黄海')) {
      fromWarehouse = warehouseLocations.xixiaier_huanghai;
    }

    // 确定调入仓库
    if (item.inWarehouse?.includes('郑州')) {
      toWarehouse = warehouseLocations.zhengzhou;
    } else if (item.inWarehouse?.includes('广州')) {
      toWarehouse = warehouseLocations.guangzhou;
    } else if (item.inWarehouse?.includes('义乌')) {
      toWarehouse = warehouseLocations.yiwu;
    } else if (item.inWarehouse?.includes('成都')) {
      toWarehouse = warehouseLocations.chengdu;
    }

    // 如果找到了对应的仓库，添加到路线分组
    if (fromWarehouse && toWarehouse) {
      const routeKey = `${fromWarehouse.id}-${toWarehouse.id}-${item.status}`;
      if (!routeGroups.has(routeKey)) {
        routeGroups.set(routeKey, {
          from: fromWarehouse,
          to: toWarehouse,
          orders: [],
          color: fromWarehouse.color,
          status: item.status
        });
      }
      routeGroups.get(routeKey)!.orders.push(item);
    } else {
      console.warn('未匹配到仓库:', {
        outWarehouse: item.outWarehouse,
        inWarehouse: item.inWarehouse,
        fromWarehouse,
        toWarehouse
      });
    }
  });

  // 转换为连线数组
  const lines: Array<RouteGroup> = Array.from(routeGroups.values());
  return lines;
};

// 获取地图区域配色
const getRegionColors = (features: any[]) => {
  const colors = ['#e2e8f0', '#f1f5f9', '#f8fafc'];
  return features.map((f: any) => ({
    name: f.properties.name,
    itemStyle: {
      areaColor: colors[Math.floor(Math.random() * colors.length)],
      borderColor: '#cbd5e1'
    }
  }));
};

interface WaitingOrderGroup {
  orders: ApiData[];
  fromCoords: [number, number];
  toCoords: [number, number];
}


// 添加统一的提示框显示函数
const showTooltip = (data: ApiData, x: number, y: number, containerRef: React.RefObject<HTMLDivElement>) => {
  hideTooltip(containerRef); // 先移除可能存在的旧提示框
  
  const transportStatus = getTransportStatus(data.createTime);
  const diffDays = getDateDifference(data.createTime);
  
  const tooltip = document.createElement('div');
  tooltip.className = 'absolute bg-white px-3 py-2 text-sm rounded shadow-lg z-50 logistics-element tooltip';
  tooltip.style.left = `${x}px`;
  tooltip.style.top = `${y}px`;
  tooltip.style.transform = 'translate(-50%, -100%)';
  tooltip.style.pointerEvents = 'none';
  
  tooltip.innerHTML = `
    <div class="font-medium mb-1">调拨单号: ${data.id}</div>
    <div>调入仓库: ${data.inWarehouse}</div>
    ${data.status === '在途' ? `
    <div>运输状态: <span style="color: ${transportStatus.color}">${transportStatus.status}</span></div>
    <div>已用时间: ${diffDays}天 ${diffDays > TRANSPORT_STATUS_CONFIG.CYCLE_DAYS ? 
      `<span style="color: ${transportStatus.color}">(超出${diffDays - TRANSPORT_STATUS_CONFIG.CYCLE_DAYS}天)</span>` : 
      diffDays > TRANSPORT_STATUS_CONFIG.STATUS.NORMAL.days ? 
      `<span style="color: ${transportStatus.color}">(需加快)</span>` : 
      '<span style="color: #22c55e">(正常)</span>'
    }</div>
    <div>待转入数量: ${data.待转入数量 || 0}</div>
    ` : `
    <div>待转出数量: ${data.待转出数量 || 0}</div>
    `}
  `;
  
  containerRef.current?.appendChild(tooltip);
};

// 添加统一的提示框隐藏函数
const hideTooltip = (containerRef: React.RefObject<HTMLDivElement>) => {
  const tooltips = containerRef.current?.querySelectorAll('.logistics-element.tooltip');
  tooltips?.forEach(tooltip => tooltip.remove());
};

// 修改车辆位置计算逻辑
const calculateVehiclePosition = (
  progress: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  cp1x: number,
  cp1y: number,
  cp2x: number,
  cp2y: number,
  index: number,
  total: number,
  isWaiting: boolean = false
): { x: number; y: number; angle: number } => {
  if (isWaiting) {
    // 待发货车辆在起点仓库下方水平排列
    const spacing = 24; // 车辆间距
    const baseX = x1 + (index - (total - 1) / 2) * spacing;
    const baseY = y1 + 20; // 在仓库下方20px
    const angle = Math.atan2(y2 - y1, x2 - x1) * (180 / Math.PI);
    return { x: baseX, y: baseY, angle };
  } else {
    // 在途车辆在线条上水平分布
    const t = progress;
    const mt = 1 - t;
    
    // 计算基础位置
    const x = mt * mt * mt * x1 + 3 * mt * mt * t * cp1x + 3 * mt * t * t * cp2x + t * t * t * x2;
    const y = mt * mt * mt * y1 + 3 * mt * mt * t * cp1y + 3 * mt * t * t * cp2y + t * t * t * y2;
    
    // 计算切线方向
    const tangentX = -3 * mt * mt * x1 + 3 * mt * (1 - 3 * t) * cp1x + 3 * t * (2 - 3 * t) * cp2x + 3 * t * t * x2;
    const tangentY = -3 * mt * mt * y1 + 3 * mt * (1 - 3 * t) * cp1y + 3 * t * (2 - 3 * t) * cp2y + 3 * t * t * y2;
    
    // 如果有多个车辆，在切线方向上均匀分布
    const spacing = 20; // 车辆间距
    const offset = (index - (total - 1) / 2) * spacing;
    const angle = Math.atan2(tangentY, tangentX) * (180 / Math.PI);
    
    // 计算偏移后的位置
    const offsetX = offset * Math.cos(angle * Math.PI / 180);
    const offsetY = offset * Math.sin(angle * Math.PI / 180);
    
    return {
      x: x + offsetX,
      y: y + offsetY,
      angle: angle
    };
  }
};

export default function LogisticsMap({ apiData = [] }: LogisticsMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chinaMapRef = useRef<HTMLDivElement>(null);
  const shanghaiMapRef = useRef<HTMLDivElement>(null);
  const [chinaInstance, setChinaInstance] = useState<echarts.ECharts | null>(null);
  const [shanghaiInstance, setShanghaiInstance] = useState<echarts.ECharts | null>(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState<string | null>(null);

  // 获取报表数据
  const { 
    data: searchData,
    isLoading: isSearchLoading,
    error: searchError,
    refetch
  } = useQuery<SearchToolResponse>({
    queryKey: ['logistics_search', '1037'],
    queryFn: async () => {
      try {
        const response = await searchToolApi.executeSearch({
          search_id: '1037',
          page_size: 50,
          page_no: 1
        });
        if (!response?.res_content?.data_list) {
          throw new Error('数据格式不正确');
        }
        return response;
      } catch (error) {
        console.error('API Error:', error);
        throw error;
      }
    },
    enabled: true,
    staleTime: 0, // 每次都重新获取
    cacheTime: 0, // 不缓存
    retry: 1,
    refetchOnMount: true,
    refetchOnWindowFocus: true
  });


  // 使用 useMemo 处理 API 数据，避免不必要的重计算
  const logisticsLines = useMemo(() => {
    // 获取并处理数据
    const rawData = searchData?.res_content?.data_list || [];

    if (!rawData.length) {
      return [];
    }

    // 转换数据
    const transformedData = transformData(rawData);

    // 处理数据
    let processedData = processApiData(transformedData);
    
    // 如果有选中的仓库，过滤数据
    if (selectedWarehouse) {
      processedData = processedData.filter(line => 
        line.from.id === selectedWarehouse
      );
    }
    
    return processedData;
  }, [searchData, selectedWarehouse]);

  // 处理仓库点击事件
  const handleWarehouseClick = (warehouseId: string) => {
    setSelectedWarehouse(prev => prev === warehouseId ? null : warehouseId);
  };

  // 初始化地图数据
  useEffect(() => {
    echarts.registerMap('china', chinaGeoJSON as any);
    echarts.registerMap('shanghai', shanghaiGeoJSON as any);
  }, []);

  // 初始化中国地图
  useEffect(() => {
    if (!chinaMapRef.current) return;
    
    const instance = echarts.init(chinaMapRef.current);
    setChinaInstance(instance);

    const option = {
      backgroundColor: '#ffffff',
      geo: {
        map: 'china',
        roam: false,
        zoom: 1.2,
        center: [104, 36],
        label: {
          show: false
        },
        itemStyle: {
          areaColor: '#f1f5f9',
          borderColor: '#cbd5e1'
        },
        emphasis: {
          disabled: true
        },
        regions: getRegionColors(chinaGeoJSON.features)
      },
      series: [
        {
          type: 'scatter',
          coordinateSystem: 'geo',
          data: Object.values(warehouseLocations)
            .filter(w => w.type === 'factory')
            .map(w => ({
              name: w.name,
              value: w.coordinates,
              itemStyle: { 
                color: selectedWarehouse === w.id ? '#ef4444' : '#10b981',
                borderColor: '#ffffff',
                borderWidth: 3,
                shadowColor: 'rgba(0, 0, 0, 0.2)',
                shadowBlur: 10
              }
            })),
          symbol: 'circle',
          symbolSize: 24,
          label: {
            show: true,
            position: 'top',
            distance: 25,
            formatter: '{b}',
            color: '#1e293b',
            fontSize: 14,
            backgroundColor: '#ffffff',
            padding: [4, 8],
            borderRadius: 4
          }
        }
      ]
    };

    instance.setOption(option);
    
    // 添加点击事件
    instance.on('click', (params) => {
      const warehouse = Object.values(warehouseLocations)
        .find(w => w.type === 'factory' && w.name === params.name);
      if (warehouse) {
        handleWarehouseClick(warehouse.id);
      }
    });
    
    return () => {
      instance.dispose();
    };
  }, [selectedWarehouse]);

  // 初始化上海地图
  useEffect(() => {
    if (!shanghaiMapRef.current) return;
    
    const instance = echarts.init(shanghaiMapRef.current);
    setShanghaiInstance(instance);

    const shOption = {
      backgroundColor: '#ffffff',
      geo: {
        map: 'shanghai',
        roam: false,
        zoom: 1.1,
        center: [121.23, 31.40],
        label: {
          show: true,
          color: '#475569',
          fontSize: 10
        },
        itemStyle: {
          areaColor: '#f1f5f9',
          borderColor: '#cbd5e1'
        },
        emphasis: {
          disabled: true
        },
        regions: getRegionColors(shanghaiGeoJSON.features)
      },
      series: [
        {
          type: 'scatter',
          coordinateSystem: 'geo',
          data: Object.values(warehouseLocations)
            .filter(w => w.type === 'retail')
            .map(w => ({
              name: w.name,
              value: w.coordinates,
              itemStyle: { 
                color: selectedWarehouse === w.id ? '#ef4444' : (w.color || '#3b82f6'),
                borderColor: '#ffffff',
                borderWidth: 3,
                shadowColor: 'rgba(0, 0, 0, 0.2)',
                shadowBlur: 10
              }
            })),
          symbol: 'circle',
          symbolSize: 20,
          label: {
            show: true,
            position: 'right',
            distance: 10,
            formatter: '{b}',
            color: '#1e293b',
            fontSize: 12,
            backgroundColor: '#ffffff',
            padding: [4, 8],
            borderRadius: 4
          }
        }
      ]
    };

    instance.setOption(shOption);
    
    // 添加点击事件
    instance.on('click', (params) => {
      const warehouse = Object.values(warehouseLocations)
        .find(w => w.type === 'retail' && w.name === params.name);
      if (warehouse) {
        handleWarehouseClick(warehouse.id);
      }
    });
    
    return () => {
      instance.dispose();
    };
  }, [selectedWarehouse]);

  // 同时调整中国地图的显示
  useEffect(() => {
    if (!chinaMapRef.current) return;
    
    const instance = echarts.init(chinaMapRef.current);
    setChinaInstance(instance);

    const option = {
      backgroundColor: '#ffffff',
      geo: {
        map: 'china',
        roam: false,
        zoom: 1.2,
        center: [104, 36],
        label: {
          show: false
        },
        itemStyle: {
          areaColor: '#f1f5f9',
          borderColor: '#cbd5e1'
        },
        emphasis: {
          disabled: true
        },
        regions: getRegionColors(chinaGeoJSON.features)
      },
      series: [
        {
          type: 'scatter',
          coordinateSystem: 'geo',
          data: Object.values(warehouseLocations)
            .filter(w => w.type === 'factory')
            .map(w => ({
              name: w.name,
              value: w.coordinates,
              itemStyle: { 
                color: '#10b981',
                borderColor: '#ffffff',
                borderWidth: 3,
                shadowColor: 'rgba(0, 0, 0, 0.2)',
                shadowBlur: 10
              }
            })),
          symbol: 'circle',
          symbolSize: 24,
          label: {
            show: true,
            position: 'top',
            distance: 25,
            formatter: '{b}',
            color: '#1e293b',
            fontSize: 14,
            backgroundColor: '#ffffff',
            padding: [4, 8],
            borderRadius: 4
          }
        }
      ]
    };

    instance.setOption(option);
    
    return () => {
      instance.dispose();
    };
  }, []);

  // 绘制连线和车辆图标
  useEffect(() => {
    if (!chinaInstance || !shanghaiInstance || !containerRef.current || !logisticsLines.length) return;

    const drawLines = () => {
      // 移除旧的 SVG 和图片元素
      const oldElements = containerRef.current?.querySelectorAll('.logistics-element');
      oldElements?.forEach(el => el.remove());

      // 创建新的 SVG
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('style', 'position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 10;');
      svg.classList.add('logistics-element');
      containerRef.current?.appendChild(svg);

      // 添加箭头定义
      const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
      defs.innerHTML = `
        <marker id="arrowhead" markerWidth="6" markerHeight="6" refX="6" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6" fill="#3b82f6"/>
        </marker>
      `;
      svg.appendChild(defs);

      // 获取容器位置信息
      const containerRect = containerRef.current?.getBoundingClientRect();
      const chinaRect = chinaMapRef.current?.getBoundingClientRect();
      const shanghaiRect = shanghaiMapRef.current?.getBoundingClientRect();

      if (!containerRect || !chinaRect || !shanghaiRect) return;

      // 添加全局待发货车辆索引
      let globalWaitingIndex = 0;

      // 绘制连线和车辆图标
      logisticsLines.forEach((line, index) => {
        let fromPixel, toPixel;

        // 根据仓库类型获取像素坐标
        if (line.from.type === 'retail') {
          fromPixel = shanghaiInstance.convertToPixel('geo', line.from.coordinates);
        } else {
          fromPixel = chinaInstance.convertToPixel('geo', line.from.coordinates);
        }

        if (line.to.type === 'retail') {
          toPixel = shanghaiInstance.convertToPixel('geo', line.to.coordinates);
        } else {
          toPixel = chinaInstance.convertToPixel('geo', line.to.coordinates);
        }

        if (!Array.isArray(fromPixel) || !Array.isArray(toPixel)) return;

        // 计算坐标
        const x1 = fromPixel[0] + (line.from.type === 'retail' ? shanghaiRect.left - containerRect.left : chinaRect.left - containerRect.left);
        const y1 = fromPixel[1] + (line.from.type === 'retail' ? shanghaiRect.top - containerRect.top : chinaRect.top - containerRect.top);
        const x2 = toPixel[0] + (line.to.type === 'retail' ? shanghaiRect.left - containerRect.left : chinaRect.left - containerRect.left);
        const y2 = toPixel[1] + (line.to.type === 'retail' ? shanghaiRect.top - containerRect.top : chinaRect.top - containerRect.top);

        // 创建连线组
        const pathGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        pathGroup.setAttribute('id', `line-group-${line.from.id}-${line.to.id}`);
        
        // 计算贝塞尔曲线的控制点
        const dx = x2 - x1;
        const dy = y2 - y1;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        // 根据起点位置调整控制点
        let offset = distance * 0.2;
        
        // 获取起点的相对垂直位置
        const verticalPosition = (y1 - containerRef.current?.getBoundingClientRect().top!) / 
                               (containerRef.current?.getBoundingClientRect().height || 1);
        
        // 根据垂直位置和运输状态调整弧度和方向
        if (verticalPosition < 0.3) { // 上部分仓库
            offset = distance * 0.35; // 较大的弧度
            // 根据状态调整偏移量
            if (line.orders[0].status === '在途') {
                offset *= 1.2; // 在途状态线条偏移量增加20%
            } else if (line.orders[0].status === '待发货') {
                offset *= 0.8; // 待发货状态线条偏移量减少20%
            }
        } else if (verticalPosition > 0.7) { // 下部分仓库
            offset = distance * 0.35; // 较大的弧度
            offset = -offset; // 反向弧度
            // 根据状态调整偏移量
            if (line.orders[0].status === '在途') {
                offset *= 1.2; // 在途状态线条偏移量增加20%
            } else if (line.orders[0].status === '待发货') {
                offset *= 0.8; // 待发货状态线条偏移量减少20%
            }
        } else { // 中部仓库
            offset = distance * 0.15; // 较小的弧度
            // 根据状态调整偏移量
            if (line.orders[0].status === '在途') {
                offset *= 1.5; // 在途状态线条偏移量增加50%
            } else if (line.orders[0].status === '待发货') {
                offset *= 0.5; // 待发货状态线条偏移量减少50%
            }
        }
        
        // 计算中点
        const midX = (x1 + x2) / 2;
        const midY = (y1 + y2) / 2;
        
        // 计算垂直于线段的方向
        const perpX = -dy / distance;
        const perpY = dx / distance;
        
        // 添加水平方向的偏移以增强椭圆效果
        const horizontalBias = (x2 > x1) ? 0.2 : -0.2; // 根据方向添加水平偏移
        
        // 根据状态调整水平偏移
        let statusHorizontalBias = horizontalBias;
        if (line.orders[0].status === '在途') {
            statusHorizontalBias *= 1.3; // 在途状态水平偏移增加30%
        } else if (line.orders[0].status === '待发货') {
            statusHorizontalBias *= 0.7; // 待发货状态水平偏移减少30%
        }
        
        // 计算控制点，加入水平偏移
        const controlX = midX + perpX * offset + dx * statusHorizontalBias;
        const controlY = midY + perpY * offset;
        
        // 创建曲线路径
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        // 使用三次贝塞尔曲线以获得更平滑的效果
        const cp1x = x1 + dx * 0.25 + perpX * offset * 0.5;
        const cp1y = y1 + dy * 0.25 + perpY * offset * 0.5;
        const cp2x = x1 + dx * 0.75 + perpX * offset * 0.5;
        const cp2y = y1 + dy * 0.75 + perpY * offset * 0.5;
        const d = `M ${x1} ${y1} C ${cp1x} ${cp1y} ${cp2x} ${cp2y} ${x2} ${y2}`;
        path.setAttribute('d', d);
        path.setAttribute('stroke', line.color);
        path.setAttribute('stroke-width', '1.5');
        path.setAttribute('fill', 'none');
        path.setAttribute('class', 'logistics-line');
        path.setAttribute('id', `line-${line.from.id}-${line.to.id}-${line.status}`);
        
        // 如果是在途状态，设置为虚线并添加透明度
        if (line.status === '待发货') {
          path.setAttribute('stroke-dasharray', '4,4');
          path.setAttribute('stroke-opacity', '0.6');
        }
        
        // 添加箭头
        const arrowMarker = document.createElementNS('http://www.w3.org/2000/svg', 'marker');
        arrowMarker.setAttribute('id', `arrowhead-${line.from.id}-${line.to.id}-${line.status}`);
        arrowMarker.setAttribute('markerWidth', '6');
        arrowMarker.setAttribute('markerHeight', '6');
        arrowMarker.setAttribute('refX', '6');
        arrowMarker.setAttribute('refY', '3');
        arrowMarker.setAttribute('orient', 'auto');
        
        const arrowPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        arrowPath.setAttribute('d', 'M0,0 L6,3 L0,6');
        arrowPath.setAttribute('fill', line.color);
        if (line.status === '待发货') {
          arrowPath.setAttribute('fill-opacity', '0.6');
        }
        arrowMarker.appendChild(arrowPath);
        
        defs.appendChild(arrowMarker);
        path.setAttribute('marker-end', `url(#arrowhead-${line.from.id}-${line.to.id}-${line.status})`);

        // 添加提示
        const title = document.createElementNS('http://www.w3.org/2000/svg', 'title');
        title.textContent = `从 ${line.from.name} 到 ${line.to.name}\n数量: ${line.orders.reduce((sum, order) => sum + (order.quantity || 0), 0)}\n状态: ${line.status}`;
        path.appendChild(title);

        pathGroup.appendChild(path);

        // 在drawLines函数内,修改车辆图标的添加逻辑
        if (line.status === '在途') {
          // 对每个订单创建对应的车辆
          line.orders.forEach((order, index) => {
            const progress = calculateTransportProgress(order.createTime);
            const position = calculateVehiclePosition(
              progress,
              x1, y1, x2, y2,
              cp1x, cp1y, cp2x, cp2y,
              index,
              line.orders.length
            );

            const truck = document.createElement('img');
            truck.src = getCarIcon(order.createTime);
            truck.style.position = 'absolute';
            truck.style.width = '16px';
            truck.style.height = '16px';
            truck.style.left = `${position.x - 8}px`;
            truck.style.top = `${position.y - 8}px`;
            truck.style.transform = `rotate(${position.angle + 180}deg)`;
            truck.style.transformOrigin = 'center center';
            truck.style.cursor = 'pointer';
            truck.style.zIndex = '20';
            truck.classList.add('logistics-element');
            truck.dataset.orderId = order.id;

            // 添加鼠标事件
            truck.addEventListener('mouseenter', () => {
              const relatedLine = document.getElementById(`line-${line.from.id}-${line.to.id}-${line.status}`);
              if (relatedLine) {
                relatedLine.setAttribute('stroke-width', '3');
                relatedLine.style.filter = 'drop-shadow(0 0 4px rgba(59, 130, 246, 0.5))';
              }
              showTooltip(order, position.x, position.y - 40, containerRef);
            });

            truck.addEventListener('mouseleave', () => {
              const relatedLine = document.getElementById(`line-${line.from.id}-${line.to.id}-${line.status}`);
              if (relatedLine) {
                relatedLine.setAttribute('stroke-width', '1.5');
                relatedLine.style.filter = 'none';
              }
              hideTooltip(containerRef);
            });

            containerRef.current?.appendChild(truck);
          });
        } else if (line.status === '待发货') {
          // 在仓库下方显示待发货车辆
          const spacing = 20; // 车辆间距
          
          // 获取当前连接线在所有待发货连接线中的索引
          const currentLineIndex = logisticsLines
            .filter(l => l.status === '待发货')
            .findIndex(l => l.from.id === line.from.id && l.to.id === line.to.id);

          
          line.orders.forEach((order: ApiData) => {
            // 使用连接线索引来计算位置
            const baseX = x1 + (currentLineIndex - 2) * spacing ; // 使用更大的间距
            const baseY = y1 + 20; // 在仓库下方20px

            const truck = document.createElement('img');
            truck.src = getCarIcon(order.createTime);
            truck.style.position = 'absolute';
            truck.style.width = '16px';
            truck.style.height = '16px';
            truck.style.left = `${baseX}px`;
            truck.style.top = `${baseY }px`;
            truck.style.transform = 'rotate(0deg)'; // 固定水平朝右方向
            truck.style.transformOrigin = 'center center';
            truck.style.cursor = 'pointer';
            truck.style.zIndex = '20';
            truck.classList.add('logistics-element');
            truck.dataset.orderId = order.id;

            // 添加鼠标事件
            truck.addEventListener('mouseenter', () => {
              const relatedLine = document.getElementById(`line-${line.from.id}-${line.to.id}-${line.status}`);
              if (relatedLine) {
                relatedLine.setAttribute('stroke-width', '3');
                relatedLine.style.filter = 'drop-shadow(0 0 4px rgba(59, 130, 246, 0.5))';
              }
              showTooltip(order, baseX, baseY - 20, containerRef);
            });

            truck.addEventListener('mouseleave', () => {
              const relatedLine = document.getElementById(`line-${line.from.id}-${line.to.id}-${line.status}`);
              if (relatedLine) {
                relatedLine.setAttribute('stroke-width', '1.5');
                relatedLine.style.filter = 'none';
              }
              hideTooltip(containerRef);
            });

            containerRef.current?.appendChild(truck);
          });
        }
        
        // 起点动画
        const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        circle.setAttribute('cx', x1.toString());
        circle.setAttribute('cy', y1.toString());
        circle.setAttribute('r', '4');
        circle.setAttribute('fill', line.color);
        
        const animate1 = document.createElementNS('http://www.w3.org/2000/svg', 'animate');
        animate1.setAttribute('attributeName', 'r');
        animate1.setAttribute('values', '4;8;4');
        animate1.setAttribute('dur', '2s');
        animate1.setAttribute('repeatCount', 'indefinite');
        
        const animate2 = document.createElementNS('http://www.w3.org/2000/svg', 'animate');
        animate2.setAttribute('attributeName', 'opacity');
        animate2.setAttribute('values', '0.6;0.2;0.6');
        animate2.setAttribute('dur', '2s');
        animate2.setAttribute('repeatCount', 'indefinite');
        
        circle.appendChild(animate1);
        circle.appendChild(animate2);
        
        pathGroup.appendChild(circle);
        svg.appendChild(pathGroup);
      });

      // 按仓库分组待发货订单
      const waitingOrdersByWarehouse = new Map<string, WaitingOrderGroup>();

      logisticsLines.forEach(line => {
        if (line.status === '待发货') {
          const warehouseId = line.from.id;
          if (!waitingOrdersByWarehouse.has(warehouseId)) {
            waitingOrdersByWarehouse.set(warehouseId, {
              orders: [],
              fromCoords: [0, 0],
              toCoords: [0, 0]
            });
          }
          const group = waitingOrdersByWarehouse.get(warehouseId)!;
          group.orders.push(line.orders[0]); // 只取第一个订单作为代表
          
          // 获取起点和终点坐标
          const fromPixel = line.from.type === 'retail' 
            ? shanghaiInstance?.convertToPixel('geo', line.from.coordinates)
            : chinaInstance?.convertToPixel('geo', line.from.coordinates);
          
          const toPixel = line.to.type === 'retail'
            ? shanghaiInstance?.convertToPixel('geo', line.to.coordinates)
            : chinaInstance?.convertToPixel('geo', line.to.coordinates);
            
          if (Array.isArray(fromPixel) && Array.isArray(toPixel)) {
            const containerRect = containerRef.current?.getBoundingClientRect();
            const fromMapRect = line.from.type === 'retail' ? shanghaiRect : chinaRect;
            const toMapRect = line.to.type === 'retail' ? shanghaiRect : chinaRect;
            
            if (containerRect && fromMapRect && toMapRect) {
              group.fromCoords = [
                fromPixel[0] + (fromMapRect.left - containerRect.left),
                fromPixel[1] + (fromMapRect.top - containerRect.top)
              ];
              group.toCoords = [
                toPixel[0] + (toMapRect.left - containerRect.left),
                toPixel[1] + (toMapRect.top - containerRect.top)
              ];
            }
          }
        }
      });
    };

    drawLines();

    // 监听地图缩放和平移事件
    const handleMapChange = () => {
      drawLines();
    };

    chinaInstance.on('georoam', handleMapChange);
    shanghaiInstance.on('georoam', handleMapChange);

    // 监听窗口大小变化
    const handleResize = () => {
      chinaInstance.resize();
      shanghaiInstance.resize();
      drawLines();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      chinaInstance.off('georoam', handleMapChange);
      shanghaiInstance.off('georoam', handleMapChange);
      window.removeEventListener('resize', handleResize);
      const svg = containerRef.current?.querySelector('svg');
      if (svg) {
        svg.remove();
      }
    };
  }, [chinaInstance, shanghaiInstance, logisticsLines]);

  return (
    <div className="min-h-screen bg-slate-50 p-8">
      <div className="max-w-[1800px] mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <MapPin className="w-6 h-6" />
            物流调拨监控
            {/* 添加数据加载状态显示 */}
            <span className="text-sm font-normal text-slate-500">
              ({logisticsLines.length} 条调拨记录)
            </span>
          </h1>
          <div className="flex gap-2">
            <Badge variant="outline" className="text-emerald-600 border-emerald-200">
              工厂仓: {Object.values(warehouseLocations).filter(w => w.type === 'factory').length}个
            </Badge>
            <Badge variant="outline" className="text-blue-600 border-blue-200">
              零售仓: {Object.values(warehouseLocations).filter(w => w.type === 'retail').length}个
            </Badge>
          </div>
        </div>

        <div ref={containerRef} className="relative grid grid-cols-2 gap-6 bg-white rounded-lg shadow-sm p-6">
          <Legend />
          <div className="col-span-1">
            <div ref={chinaMapRef} style={{ height: '750px' }} />
          </div>
          <div className="col-span-1">
            <div ref={shanghaiMapRef} style={{ height: '750px' }} />
          </div>
        </div>
      </div>
    </div>
  );
} 